'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { 
  Type, 
  Wand2,
  Copy,
  CheckCircle,
  Save,
  Download,
  Plus,
  Trash2,
  Eye,
  Monitor,
  Smartphone,
  Upload
} from 'lucide-react'
import { Brand } from '../../utils/mockData'
import { copyToClipboard } from '../../utils/clipboard'

interface TypographySystemProps {
  brand: Brand
}

interface FontPairing {
  name: string
  primary: string
  secondary: string
  description: string
  category: string
}

interface Typography {
  primary: {
    name: string
    family: string
    weights: string[]
    source: 'google' | 'custom' | 'system'
  }
  secondary: {
    name: string
    family: string
    weights: string[]
    source: 'google' | 'custom' | 'system'
  }
  hierarchy: {
    h1: { size: string; weight: string; lineHeight: string; usage: string }
    h2: { size: string; weight: string; lineHeight: string; usage: string }
    h3: { size: string; weight: string; lineHeight: string; usage: string }
    h4: { size: string; weight: string; lineHeight: string; usage: string }
    body: { size: string; weight: string; lineHeight: string; usage: string }
    caption: { size: string; weight: string; lineHeight: string; usage: string }
  }
  guidelines: {
    spacing: string
    maxLineLength: string
    accessibility: string[]
  }
}

const SUGGESTED_FONT_PAIRINGS: FontPairing[] = [
  {
    name: 'Modern Professional',
    primary: 'Inter',
    secondary: 'Inter',
    description: 'Clean and highly readable for digital interfaces',
    category: 'Sans-serif'
  },
  {
    name: 'Classic Elegance',
    primary: 'Playfair Display',
    secondary: 'Source Sans Pro',
    description: 'Sophisticated serif with clean sans-serif support',
    category: 'Serif + Sans'
  },
  {
    name: 'Tech Startup',
    primary: 'Poppins',
    secondary: 'Roboto',
    description: 'Modern and friendly for technology brands',
    category: 'Sans-serif'
  },
  {
    name: 'Creative Agency',
    primary: 'Montserrat',
    secondary: 'Open Sans',
    description: 'Bold and creative with excellent readability',
    category: 'Sans-serif'
  },
  {
    name: 'Editorial Style',
    primary: 'Lora',
    secondary: 'Nunito Sans',
    description: 'Editorial serif paired with modern sans-serif',
    category: 'Serif + Sans'
  },
  {
    name: 'Minimalist',
    primary: 'Work Sans',
    secondary: 'Work Sans',
    description: 'Clean minimalist approach with single font family',
    category: 'Sans-serif'
  }
]

const GOOGLE_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Source Sans Pro', 'Nunito Sans', 'Work Sans', 'DM Sans',
  'Playfair Display', 'Lora', 'Merriweather', 'Crimson Text',
  'JetBrains Mono', 'Fira Code', 'Source Code Pro'
]

const FONT_WEIGHTS = ['100', '200', '300', '400', '500', '600', '700', '800', '900']

export function TypographySystem({ brand }: TypographySystemProps) {
  const [typography, setTypography] = useState<Typography>({
    primary: {
      name: 'Inter',
      family: 'Inter, sans-serif',
      weights: ['400', '500', '600', '700'],
      source: 'google'
    },
    secondary: {
      name: 'Inter',
      family: 'Inter, sans-serif',
      weights: ['400', '500'],
      source: 'google'
    },
    hierarchy: {
      h1: { size: '3rem', weight: '700', lineHeight: '1.2', usage: 'Page titles, hero headlines' },
      h2: { size: '2.25rem', weight: '600', lineHeight: '1.3', usage: 'Section headers' },
      h3: { size: '1.875rem', weight: '600', lineHeight: '1.4', usage: 'Subsection headers' },
      h4: { size: '1.5rem', weight: '500', lineHeight: '1.4', usage: 'Component titles' },
      body: { size: '1rem', weight: '400', lineHeight: '1.6', usage: 'Body text, paragraphs' },
      caption: { size: '0.875rem', weight: '400', lineHeight: '1.5', usage: 'Captions, small text' }
    },
    guidelines: {
      spacing: '1.5rem',
      maxLineLength: '65ch',
      accessibility: ['Minimum 16px for body text', '4.5:1 contrast ratio', 'Line height 1.5+']
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [previewText, setPreviewText] = useState('The quick brown fox jumps over the lazy dog')
  const [selectedDevice, setSelectedDevice] = useState<'desktop' | 'mobile'>('desktop')

  useEffect(() => {
    loadTypographyData()
  }, [brand.id])

  const loadTypographyData = async () => {
    setIsLoading(true)
    try {
      const savedData = localStorage.getItem(`typography_${brand.id}`)
      if (savedData) {
        setTypography(JSON.parse(savedData))
      }
    } catch (error) {
      console.error('Error loading typography data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveTypographyData = async () => {
    setIsSaving(true)
    try {
      localStorage.setItem(`typography_${brand.id}`, JSON.stringify(typography))
      setLastSaved(new Date())
    } catch (error) {
      console.error('Error saving typography data:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const applyFontPairing = (pairing: FontPairing) => {
    setTypography(prev => ({
      ...prev,
      primary: {
        ...prev.primary,
        name: pairing.primary,
        family: `${pairing.primary}, ${pairing.category.includes('Sans') ? 'sans-serif' : 'serif'}`
      },
      secondary: {
        ...prev.secondary,
        name: pairing.secondary,
        family: `${pairing.secondary}, ${pairing.category.includes('Sans') ? 'sans-serif' : 'serif'}`
      }
    }))
  }

  const updateHierarchy = (level: keyof Typography['hierarchy'], field: string, value: string) => {
    setTypography(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        [level]: {
          ...prev.hierarchy[level],
          [field]: value
        }
      }
    }))
  }

  const updateFont = (type: 'primary' | 'secondary', field: string, value: any) => {
    setTypography(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }))
  }

  const generateAITypography = async () => {
    setIsLoading(true)
    try {
      // Mock AI typography generation based on brand industry
      const industryTypographyMap: Record<string, { primary: string; secondary: string }> = {
        'Technology': { primary: 'Inter', secondary: 'Roboto' },
        'Healthcare': { primary: 'Source Sans Pro', secondary: 'Open Sans' },
        'Finance': { primary: 'Work Sans', secondary: 'Lato' },
        'Food & Beverage': { primary: 'Poppins', secondary: 'Nunito Sans' },
        'Fashion': { primary: 'Montserrat', secondary: 'Lora' },
        'Education': { primary: 'Lato', secondary: 'Merriweather' }
      }

      const industry = brand.metadata?.industry || 'Technology'
      const suggestion = industryTypographyMap[industry] || industryTypographyMap['Technology']

      setTypography(prev => ({
        ...prev,
        primary: {
          ...prev.primary,
          name: suggestion.primary,
          family: `${suggestion.primary}, sans-serif`
        },
        secondary: {
          ...prev.secondary,
          name: suggestion.secondary,
          family: `${suggestion.secondary}, sans-serif`
        }
      }))
    } catch (error) {
      console.error('Error generating AI typography:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const exportTypography = () => {
    const typographyData = {
      brand: brand.name,
      typography,
      exported: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(typographyData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${brand.name.toLowerCase().replace(/\s+/g, '-')}-typography.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const copyCSS = async () => {
    const css = `
/* Typography System for ${brand.name} */
:root {
  --font-primary: ${typography.primary.family};
  --font-secondary: ${typography.secondary.family};
}

h1 {
  font-family: var(--font-primary);
  font-size: ${typography.hierarchy.h1.size};
  font-weight: ${typography.hierarchy.h1.weight};
  line-height: ${typography.hierarchy.h1.lineHeight};
}

h2 {
  font-family: var(--font-primary);
  font-size: ${typography.hierarchy.h2.size};
  font-weight: ${typography.hierarchy.h2.weight};
  line-height: ${typography.hierarchy.h2.lineHeight};
}

h3 {
  font-family: var(--font-primary);
  font-size: ${typography.hierarchy.h3.size};
  font-weight: ${typography.hierarchy.h3.weight};
  line-height: ${typography.hierarchy.h3.lineHeight};
}

h4 {
  font-family: var(--font-primary);
  font-size: ${typography.hierarchy.h4.size};
  font-weight: ${typography.hierarchy.h4.weight};
  line-height: ${typography.hierarchy.h4.lineHeight};
}

body, p {
  font-family: var(--font-secondary);
  font-size: ${typography.hierarchy.body.size};
  font-weight: ${typography.hierarchy.body.weight};
  line-height: ${typography.hierarchy.body.lineHeight};
}

.caption {
  font-family: var(--font-secondary);
  font-size: ${typography.hierarchy.caption.size};
  font-weight: ${typography.hierarchy.caption.weight};
  line-height: ${typography.hierarchy.caption.lineHeight};
}
    `.trim()

    try {
      await copyToClipboard(css) // Will show toast notification by default
    } catch (error) {
      console.error('Failed to copy CSS:', error)
    }
  }

  if (isLoading && !typography.primary.name) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 bg-blue-600 rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-slate-600">Loading typography system...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Typography System</h2>
          <p className="text-slate-600">Define your brand's typography hierarchy and font usage</p>
        </div>
        <div className="flex items-center space-x-3">
          {lastSaved && (
            <div className="flex items-center space-x-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Saved {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
          <Button 
            variant="outline"
            onClick={copyCSS}
            size="sm"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy CSS
          </Button>
          <Button 
            variant="outline"
            onClick={exportTypography}
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            onClick={saveTypographyData}
            disabled={isSaving}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Typography'}
          </Button>
        </div>
      </div>

      {/* AI Typography Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            AI Font Recommendations
          </CardTitle>
          <CardDescription>
            Get AI-powered font recommendations based on your industry and brand personality
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <Button 
              onClick={generateAITypography}
              disabled={isLoading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {isLoading ? 'Generating...' : 'Generate AI Suggestions'}
            </Button>
            <span className="text-sm text-slate-600">
              Based on {brand.metadata?.industry || 'Technology'} industry
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SUGGESTED_FONT_PAIRINGS.map((pairing, index) => (
              <Card 
                key={index} 
                className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" 
                onClick={() => applyFontPairing(pairing)}
              >
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">{pairing.name}</h4>
                    <div className="space-y-1">
                      <div style={{ fontFamily: pairing.primary }} className="text-lg font-semibold">
                        {pairing.primary}
                      </div>
                      <div style={{ fontFamily: pairing.secondary }} className="text-sm text-slate-600">
                        {pairing.secondary}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">{pairing.description}</p>
                    <Badge variant="outline" className="text-xs">{pairing.category}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Font Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Type className="w-5 h-5" />
              Font Selection
            </CardTitle>
            <CardDescription>
              Choose your primary and secondary fonts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Primary Font */}
            <div className="space-y-3">
              <Label>Primary Font (Headers)</Label>
              <Select 
                value={typography.primary.name} 
                onValueChange={(value) => updateFont('primary', 'name', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOOGLE_FONTS.map(font => (
                    <SelectItem key={font} value={font}>{font}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="space-y-2">
                <Label>Font Weights</Label>
                <div className="flex flex-wrap gap-2">
                  {FONT_WEIGHTS.map(weight => (
                    <Button
                      key={weight}
                      size="sm"
                      variant={typography.primary.weights.includes(weight) ? "default" : "outline"}
                      onClick={() => {
                        const weights = typography.primary.weights.includes(weight)
                          ? typography.primary.weights.filter(w => w !== weight)
                          : [...typography.primary.weights, weight]
                        updateFont('primary', 'weights', weights)
                      }}
                      className="h-8 px-3 text-xs"
                    >
                      {weight}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <Separator />

            {/* Secondary Font */}
            <div className="space-y-3">
              <Label>Secondary Font (Body Text)</Label>
              <Select 
                value={typography.secondary.name} 
                onValueChange={(value) => updateFont('secondary', 'name', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOOGLE_FONTS.map(font => (
                    <SelectItem key={font} value={font}>{font}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="space-y-2">
                <Label>Font Weights</Label>
                <div className="flex flex-wrap gap-2">
                  {FONT_WEIGHTS.map(weight => (
                    <Button
                      key={weight}
                      size="sm"
                      variant={typography.secondary.weights.includes(weight) ? "default" : "outline"}
                      onClick={() => {
                        const weights = typography.secondary.weights.includes(weight)
                          ? typography.secondary.weights.filter(w => w !== weight)
                          : [...typography.secondary.weights, weight]
                        updateFont('secondary', 'weights', weights)
                      }}
                      className="h-8 px-3 text-xs"
                    >
                      {weight}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Typography Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Live Preview
            </CardTitle>
            <CardDescription>
              See how your typography looks in action
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant={selectedDevice === 'desktop' ? 'default' : 'outline'}
                onClick={() => setSelectedDevice('desktop')}
              >
                <Monitor className="w-4 h-4 mr-1" />
                Desktop
              </Button>
              <Button
                size="sm"
                variant={selectedDevice === 'mobile' ? 'default' : 'outline'}
                onClick={() => setSelectedDevice('mobile')}
              >
                <Smartphone className="w-4 h-4 mr-1" />
                Mobile
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Preview Text</Label>
              <Textarea
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                rows={2}
                placeholder="Enter text to preview..."
              />
            </div>

            <div className={`space-y-4 p-4 bg-white border rounded-lg ${selectedDevice === 'mobile' ? 'max-w-sm' : ''}`}>
              <div 
                style={{ 
                  fontFamily: typography.primary.family,
                  fontSize: selectedDevice === 'mobile' ? '2rem' : typography.hierarchy.h1.size,
                  fontWeight: typography.hierarchy.h1.weight,
                  lineHeight: typography.hierarchy.h1.lineHeight
                }}
              >
                {previewText}
              </div>
              <div 
                style={{ 
                  fontFamily: typography.primary.family,
                  fontSize: selectedDevice === 'mobile' ? '1.5rem' : typography.hierarchy.h2.size,
                  fontWeight: typography.hierarchy.h2.weight,
                  lineHeight: typography.hierarchy.h2.lineHeight
                }}
              >
                {previewText}
              </div>
              <div 
                style={{ 
                  fontFamily: typography.secondary.family,
                  fontSize: selectedDevice === 'mobile' ? '0.875rem' : typography.hierarchy.body.size,
                  fontWeight: typography.hierarchy.body.weight,
                  lineHeight: typography.hierarchy.body.lineHeight
                }}
              >
                {previewText}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Typography Hierarchy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="w-5 h-5" />
            Typography Hierarchy
          </CardTitle>
          <CardDescription>
            Define sizes, weights, and usage for each text level
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.entries(typography.hierarchy).map(([level, styles]) => (
              <div key={level} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                <div className="md:col-span-1">
                  <Label className="uppercase text-xs font-semibold text-slate-500">
                    {level}
                  </Label>
                  <div 
                    className="mt-1"
                    style={{ 
                      fontFamily: level.startsWith('h') ? typography.primary.family : typography.secondary.family,
                      fontSize: styles.size,
                      fontWeight: styles.weight,
                      lineHeight: styles.lineHeight
                    }}
                  >
                    Sample Text
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Size</Label>
                  <Input
                    value={styles.size}
                    onChange={(e) => updateHierarchy(level as keyof Typography['hierarchy'], 'size', e.target.value)}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Weight</Label>
                  <Select 
                    value={styles.weight} 
                    onValueChange={(value) => updateHierarchy(level as keyof Typography['hierarchy'], 'weight', value)}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_WEIGHTS.map(weight => (
                        <SelectItem key={weight} value={weight}>{weight}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Line Height</Label>
                  <Input
                    value={styles.lineHeight}
                    onChange={(e) => updateHierarchy(level as keyof Typography['hierarchy'], 'lineHeight', e.target.value)}
                    className="h-8"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Usage</Label>
                  <Input
                    value={styles.usage}
                    onChange={(e) => updateHierarchy(level as keyof Typography['hierarchy'], 'usage', e.target.value)}
                    className="h-8"
                    placeholder="When to use this style..."
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Typography Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Typography Guidelines
          </CardTitle>
          <CardDescription>
            Best practices and accessibility considerations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Usage Guidelines</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Use {typography.primary.name} for all headings and emphasis</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Use {typography.secondary.name} for body text and paragraphs</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Maintain consistent line spacing of {typography.guidelines.spacing}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Limit line length to {typography.guidelines.maxLineLength} for readability</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Accessibility Standards</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                {typography.guidelines.accessibility.map((guideline, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{guideline}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}